import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosClient from '../api/axiosClient';
import { storeAdminProfile, storeApiKey, clearSession } from '../utils/storage';

const LoginContainer = styled.div`
  display: grid;
  place-items: center;
  height: 100vh;
  min-height: 100vh;
  width: 100vw;
  padding: 0;
  background: #f3f4f7;
  font-family: 'Space Grotesk', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  overflow: hidden;
`;

const LoginCard = styled.div`
  display: flex;
  flex-direction: row;
  width: min(100vw, 800px);
  height: min(500px, 88vh);
  background: #ffffff;
  border-radius: 22px;
  box-shadow: 0 26px 95px rgba(15, 23, 42, 0.16);
  overflow: hidden;

  @media (max-width: 860px) {
    width: min(100vw, 760px);
    height: min(480px, 88vh);
  }
`;

const InfoPanel = styled.section`
  flex: 1;
  background: #0f172a;
  color: #e2e8f0;
  padding: clamp(28px, 5vh, 42px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const BrandBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 999px;
  background: rgba(226, 232, 240, 0.1);
  color: #f8fafc;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const InfoTitle = styled.h1`
  font-size: 28px;
  line-height: 1.2;
  margin: 20px 0 10px;
  color: #f8fafc;
`;

const InfoText = styled.p`
  color: #cbd5f5;
  font-size: 13px;
  margin-bottom: 26px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
  color: #e2e8f0;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 13px;
  }

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #38bdf8;
  }
`;

const InfoFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: #94a3b8;
`;

const InfoMeta = styled.p`
  margin: 0;
`;

const FormPanel = styled.section`
  flex: 1;
  padding: clamp(28px, 5vh, 44px) clamp(24px, 6vw, 52px);
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 900px) {
    padding: 28px;
  }

  @media (max-width: 760px) {
    flex: unset;
    width: 100%;
    padding: 28px;
  }
`;

const Title = styled.h2`
  color: #0f172a;
  font-size: 26px;
  font-weight: 700;
  margin-bottom: 6px;
`;

const Subtitle = styled.p`
  color: #475467;
  font-size: 13px;
  margin-bottom: 26px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #333;
  font-size: 14px;
  font-weight: 600;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 11px 15px;
  padding-right: ${props => props.$hasIcon ? '45px' : '15px'};
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  font-size: 13px;
  transition: all 0.2s ease;
  outline: none;
  background: #f9fafb;

  &:focus {
    border-color: #111322;
    background: #fff;
    box-shadow: 0 5px 18px rgba(17, 19, 34, 0.08);
  }

  &::placeholder {
    color: #98a2b3;
  }
`;

const IconButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: color 0.2s ease;

  &:hover {
    color: #111322;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Button = styled.button`
  background: #111322;
  color: white;
  padding: 13px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  margin-top: 8px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 30px rgba(17, 19, 34, 0.25);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
  border: 1px solid #fcc;
`;

const ForgotPassword = styled.a`
  color: #111322;
  font-size: 14px;
  text-align: right;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #2563eb;
  }
`;

const Footer = styled.div`
  margin-top: 26px;
  text-align: center;
  color: #475467;
  font-size: 14px;
`;

const FooterLink = styled.a`
  color: #111322;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(226, 232, 240, 0.2);
  margin: 36px 0;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #475467;
`;

const HelperText = styled.span`
  color: #475467;
`;

const Login = ({ onAuthenticated }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const handleSessionExpired = () => {
      const message = 'Session expired. Please sign in again.';
      setError(message);
      
      toast.warning(message);
    };

    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    

    if (!formData.username || !formData.password) {
      const message = 'Please fill in all fields';
      setError(message);
      toast.error(message);
      return;
    }

    setIsLoading(true);

    try {
      const response = await axiosClient.post('/api/admin/login', formData, {
        skipAuth: true
      });

      const payload = response.data;

      if (payload.success) {
        const { api_key, password: _ignored, ...profile } = payload.data;
        storeAdminProfile(profile);
        storeApiKey(api_key);
        if (typeof onAuthenticated === 'function') {
          onAuthenticated(profile);
        }
      } else {
        clearSession();
        const message = payload.message || 'Login failed';
        setError(message);
        toast.error(message);
      }
    } catch (err) {
      clearSession();
      const message = err?.response?.data?.message || 'Unable to connect to server. Please try again later.';
      setError(message);
      toast.error(message);
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <LoginContainer>
        <LoginCard>
        <InfoPanel>
          <div>
            <BrandBadge>Finance Advisor Suite</BrandBadge>
            <InfoTitle>Command every portfolio with confidence.</InfoTitle>
            <InfoText>
              Secure oversight of performance metrics, risk alerts, and compliance workflows
              from a single executive console.
            </InfoText>
            <FeatureList>
              <li><span />Real-time liquidity monitoring</li>
              <li><span />Role-based approvals</li>
              <li><span />Enterprise-grade encryption</li>
            </FeatureList>
          </div>
          <Divider />
          <InfoFooter>
            <InfoMeta>24/7 SOC monitoring included with every license.</InfoMeta>
            <InfoMeta>Need help? concierge@financeadvisor.io</InfoMeta>
          </InfoFooter>
        </InfoPanel>

        <FormPanel>
          <Title>Welcome back</Title>
          <Subtitle>Sign in to access your analytics workspace.</Subtitle>

          <Form onSubmit={handleSubmit}>
            {error && <ErrorMessage>{error}</ErrorMessage>}

            <InputGroup>
              <Label htmlFor="username">Username or Email</Label>
              <InputWrapper>
                <Input
                  type="text"
                  id="username"
                  name="username"
                  placeholder="Enter your username"
                  value={formData.username}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </InputWrapper>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="password">Password</Label>
              <InputWrapper>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isLoading}
                  $hasIcon={true}
                />
                <IconButton
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </IconButton>
              </InputWrapper>
            </InputGroup>

            <ActionsRow>
              <HelperText>Single sign-on enforced</HelperText>
              <ForgotPassword href="#!">Forgot password?</ForgotPassword>
            </ActionsRow>

            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </Form>

          <Footer>
            Developed by <FooterLink as="span">Eng. NMM. Nuhman</FooterLink>
          </Footer>
        </FormPanel>
      </LoginCard>
      </LoginContainer>
      <ToastContainer position="top-right" autoClose={3000} newestOnTop closeOnClick pauseOnHover={false} />
    </>
  );
};

export default Login;
